<template>
  <section class="mb-3">
    <section-header size="sm" class="mb-3">
      <heading :level="4" size="sm" qaid="warranty-claim-line-attachments" v-html="bp('attachments')" />
    </section-header>
    <div class="grid-x grid-margin-x mb-3">
      <div class="cell large-4" :class="{'has-error': !!errors.first(inputName('proofPurchaseValidator'))}">
        <div class="text-small flex" qaid="warranty-claim-line-attachments-proof-label">
          <span v-html="bp('proofPurchase')" />
          <span v-if="requireProofImage" class="text-red ml-25">
            *
            <span class="show-for-sr" v-html="bp('required')" />
          </span>
        </div>
        <file-upload
          qaid="warranty-claim-line-attachments-proof"
          :click-to-upload-msg="bp('uploadImage')"
          :help="null"
          :in-file-name="hasProofImage ? claimLine.warrantyPurchaseProofImage.name : null"
          :in-preset-image-file-url="hasProofImage ? claimLine.warrantyPurchaseProofImage.url : null"
          :input-id="`${claimIndex}_warrantyPurchaseProofImage`"
          accept="image/png, image/gif, image/jpeg, image/webp, application/pdf"
          extensions="gif, jpg, jpeg, png, webp, pdf"
          :upload-on-input="true"
          :upload-button="false"
          :has-error="!!errors.first(inputName('proofPurchaseValidator'))"
          :base-properties="baseProperties"
          @file-uploaded="setImage({property: 'warrantyPurchaseProofImage'}, $event)"
          @clear-file="clearImage('warrantyPurchaseProofImage')"
        />
        <input
          v-validate="requireProofImage ? 'required' : ''"
          type="hidden"
          :value="claimLine.warrantyPurchaseProofImage ? claimLine.warrantyPurchaseProofImage.name : ''"
          :name="inputName('proofPurchaseValidator')"
          :data-vv-as="bp('proofPurchase')"
        />
        <div
          v-show="errors.first(inputName('proofPurchaseValidator'))"
          class="form-feedback is-invalid"
          v-html="errors.first(inputName('proofPurchaseValidator'))"
        />
      </div>
      <div class="cell large-4" :class="{'has-error': !!errors.first(inputName('damageImageValidator'))}">
        <div class="text-small flex" qaid="warranty-claim-line-attachments-damage-label">
          <span v-html="bp('damage')" />
          <span v-if="requireDamageImage" class="text-red ml-25">
            *
            <span class="show-for-sr" v-html="bp('required')" />
          </span>
        </div>
        <file-upload
          qaid="warranty-claim-line-attachments-damage"
          :click-to-upload-msg="bp('uploadImage')"
          :has-error="showDamageImageWarning || !!errors.first(inputName('damageImageValidator'))"
          :help="null"
          :in-file-name="hasDamageImage ? claimLine.warrantyDamageImage.name : null"
          :in-preset-image-file-url="hasDamageImage ? claimLine.warrantyDamageImage.url : null"
          :input-id="`${claimIndex}_warrantyDamageImage`"
          :upload-on-input="true"
          :upload-button="false"
          :base-properties="baseProperties"
          @file-uploaded="setImage({property: 'warrantyDamageImage'}, $event)"
          @clear-file="clearImage('warrantyDamageImage')"
        />
        <input
          v-validate="requireDamageImage ? 'required' : ''"
          type="hidden"
          :value="claimLine.warrantyDamageImage ? claimLine.warrantyDamageImage.name : ''"
          :name="inputName('damageImageValidator')"
          :data-vv-as="bp('damage')"
        />
        <div
          v-show="errors.first(inputName('damageImageValidator'))"
          class="form-feedback is-invalid"
          v-html="errors.first(inputName('damageImageValidator'))"
        />
      </div>
    </div>
    <div class="grid-x grid-margin-x mb-5">
      <div
        v-for="(n, index) in totalAttachments"
        :key="`${index}-${getAttachmentByIndex(index).name}`"
        class="cell large-4"
      >
        <div
          class="text-small"
          :qaid="`warranty-claim-line-attachments-image-${index}-label`"
          v-html="bp('imageNumber', index + 1)"
        />
        <file-upload
          :click-to-upload-msg="bp('uploadImage')"
          :help="null"
          :has-error="showAttachmentImageWarning && index === 0"
          :in-file-name="getAttachmentByIndex(index).name"
          :in-preset-image-file-url="getAttachmentByIndex(index).url"
          :input-id="`${claimIndex}_warrantyOptionalAttachments_${index}`"
          :qaid="`warranty-claim-line-attachments-image-${index + 1}`"
          :upload-on-input="true"
          :upload-button="false"
          :base-properties="baseProperties"
          @file-uploaded="setImage({property: 'warrantyOptionalAttachments', index: index}, $event)"
          @clear-file="clearImage('warrantyOptionalAttachments', index)"
        />
      </div>
    </div>
    <div v-if="allowToAddAttachments" class="mt-3">
      <trek-button
        secondary
        icon="add_circle_outline"
        qaid="add-more-photos-button"
        :disabled="exceededLimitOfAttachments"
        @click.prevent="addMoreAttachments"
      >
        <span v-html="bp('addMorePhotos')"></span>
      </trek-button>
      <p v-if="exceededLimitOfAttachments">
        {{ bp('maxPhotos') }}
      </p>
    </div>
  </section>
</template>

<script>
import SectionHeader from '@/components/SectionHeader';
import Heading from '@/components/Heading';
import FileUpload from '@/components/FileUpload';
import get from 'lodash/get';
import includes from 'lodash/includes';
import {mapState, mapActions} from 'vuex';

export default {
  components: {FileUpload, SectionHeader, Heading},
  inject: ['$validator'],

  props: {
    baseProperties: {
      type: Array,
      required: true,
    },
    claimLine: {
      type: Object,
      required: true,
    },
    claimIndex: {
      type: Number,
      required: true,
    },
    maxAttachments: {
      type: Number,
      default: 18,
    },
    minAttachments: {
      type: Number,
      default: 3,
    },
    attachmentsToAddAtTime: {
      type: Number,
      default: 3,
    },
    allowToAddAttachments: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      totalAttachments: 0,
    };
  },

  computed: {
    exceededLimitOfAttachments() {
      return this.totalAttachments >= this.maxAttachments;
    },

    hasProofImage() {
      return get(this, 'claimLine.warrantyPurchaseProofImage.url.length', 0) > 0;
    },

    hasDamageImage() {
      return get(this, 'claimLine.warrantyDamageImage.url.length', 0) > 0;
    },

    showDamageImageWarning() {
      return this.claimLine ? includes(this.claimLine.waitingCodeList, 'D') : false;
    },

    showAttachmentImageWarning() {
      return this.claimLine ? includes(this.claimLine.waitingCodeList, 'I') : false;
    },

    requireProofImage() {
      const requiredByIssueType = !!(
        this.claimLine.warrantyRequiredImagesList &&
        includes(this.claimLine.warrantyRequiredImagesList, 'EnumWarrantyRequiredImages001')
      );

      return this.claimLine.selectedDamagedOccurred === 'enumDamageOccurredAfterPurchase' || requiredByIssueType;
    },

    requireDamageImage() {
      const requiredByIssueType = !!(
        this.claimLine.warrantyRequiredImagesList &&
        includes(this.claimLine.warrantyRequiredImagesList, 'EnumWarrantyRequiredImages002')
      );

      return requiredByIssueType;
    },

    ...mapState('warrantyClaims', ['validators']),
  },

  mounted() {
    const currentAttachmentsCount = !this.claimLine.warrantyOptionalAttachments
      ? 0
      : this.claimLine.warrantyOptionalAttachments.length;

    this.totalAttachments = Math.max(currentAttachmentsCount, this.minAttachments);
    this.validators.push(this.$validator);
  },

  methods: {
    addMoreAttachments() {
      this.totalAttachments += this.attachmentsToAddAtTime;
      if (this.totalAttachments > this.maxAttachments) {
        this.totalAttachments = this.maxAttachments;
      }
    },

    inputName(propertyName) {
      return `cl${this.claimIndex}_${propertyName}`;
    },

    clearImage(property, index) {
      const newImage = {
        encodedImage: null,
        name: null,
        url: null,
      };

      if (property === 'warrantyOptionalAttachments') {
        this.claimLine.warrantyOptionalAttachments[index] = newImage;
      } else {
        this.claimLine[property] = newImage;
      }

      this.$nextTick(() => {
        this.$validator.validateAll();
        this.saveImageInput().then(() => {
          this.cleanupCells();
        });
      });
    },

    cleanupCells() {
      const emptyCells = this.getEmptyCells();
      if (this.totalAttachments - this.attachmentsToAddAtTime >= emptyCells) {
        const evenRows = !(this.totalAttachments % this.attachmentsToAddAtTime);
        let removeCount = 0;

        if (evenRows) {
          removeCount = emptyCells >= this.attachmentsToAddAtTime ? this.attachmentsToAddAtTime : 0;
        } else {
          removeCount =
            emptyCells > this.attachmentsToAddAtTime ? emptyCells - this.attachmentsToAddAtTime : emptyCells;
        }

        this.totalAttachments -= removeCount;
      }
    },

    getEmptyCells() {
      if (!this.claimLine || !this.claimLine.warrantyOptionalAttachments) {
        return 0;
      }

      let notEmpty = 0;
      this.claimLine.warrantyOptionalAttachments.forEach((attachment) => {
        notEmpty += !attachment.url && !attachment.attachment ? 0 : 1;
      });

      return this.totalAttachments - notEmpty;
    },

    getAttachmentByIndex(index) {
      return get(this.claimLine, `warrantyOptionalAttachments[${index}]`, {});
    },

    setImage({property, index = null}, {name, encodedFile}) {
      const image = {encodedFile, name, url: null};

      if (property === 'warrantyOptionalAttachments') {
        this.claimLine[property][index] = image;
      } else {
        this.claimLine[property] = image;
      }

      this.$nextTick(() => this.$validator.validateAll());

      this.saveImageInput();
    },

    ...mapActions('warrantyClaims', ['saveImageInput']),
  },
};
</script>
